import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AccountNameEntry,
  InvoiceItem,
  InvoiceLevelPayNowEvent,
} from './obpp-invoice-summary.models';

// Re-export so any consumer importing from this file still compiles.
export type { InvoiceLevelPayNowEvent } from './obpp-invoice-summary.models';

@Component({
  selector: 'app-obpp-invoice-summary',
  templateUrl: './obpp-invoice-summary.component.html',
  styleUrls: ['./obpp-invoice-summary.component.scss'],
})
export class OBPPInvoiceSummaryComponent implements OnInit, OnChanges, OnDestroy {

  // ─── Inputs ──────────────────────────────────────────────────────────────
  @Input() invoiceData: InvoiceItem[] = [];
  @Input() accountName: AccountNameEntry[] = [];
  @Input() isViewOnly: boolean = false;

  // ─── Outputs ─────────────────────────────────────────────────────────────
  @Output() payNowClicked = new EventEmitter<InvoiceLevelPayNowEvent>();
  @Output() requestInvoicePDF = new EventEmitter<string>();

  /**
   * Working copy — the single source of truth for all UI state.
   * Deep-cloned from invoiceData with payment fields normalised on each reset.
   * invoiceData is never mutated.
   */
  accountsList: InvoiceItem[] = [];

  /**
   * Rows currently visible after search / currency / date filters.
   * Holds references to the same objects as accountsList, so selection
   * state stays in sync and survives filter changes.
   */
  filteredList: InvoiceItem[] = [];

  /** Account accordion headers that still have at least one visible invoice. */
  visibleAccountName: AccountNameEntry[] = [];

  // ─── Search / filter state (mirrors PIN-level view) ──────────────────────
  searchField: string = '';
  isCAD: boolean = true;
  isUSD: boolean = true;
  /** 0 = all, otherwise number of past months to include */
  invoiceDateRange: number = 0;

  // ─── Selection / payment state ───────────────────────────────────────────
  checkAllInvoices: boolean = false;
  paymentCurrency: string = '';
  displayBalDueErrMsg: boolean = false;

  // ─── Pagination ──────────────────────────────────────────────────────────
  p: number = 1;

  constructor() {}

  ngOnInit(): void {
    this.resetToInputData();
  }

  ngOnDestroy(): void {
    // nothing — dispute-tab visibility is owned by AccountSummarySwitchComponent
  }

  ngOnChanges(changes: SimpleChanges): void {
    const invoiceChanged = changes['invoiceData'] && !changes['invoiceData'].firstChange;
    const accountChanged = changes['accountName'] && !changes['accountName'].firstChange;
    if (invoiceChanged) {
      this.resetToInputData();
    } else if (accountChanged) {
      this.applyFilters();
    }
  }

  // ─── Initialisation ──────────────────────────────────────────────────────

  /**
   * Deep-clone invoiceData into accountsList and normalise all mutable UI
   * fields to a known initial state. invoiceData is never touched after this.
   */
  private resetToInputData(): void {
    this.accountsList = this.invoiceData.map((inv) => ({
      ...(JSON.parse(JSON.stringify(inv)) as InvoiceItem),
      paymentAmount: '0.00',
      payAmount: '0.00',
      isChecked: false,
    }));
    this.checkAllInvoices = false;
    this.paymentCurrency = '';
    this.applyFilters();
  }

  // ─── Search / filters ────────────────────────────────────────────────────

  /**
   * Rebuild filteredList from accountsList using the current search term,
   * currency checkboxes and date range. Never mutates accountsList.
   */
  applyFilters(): void {
    const term = (this.searchField || '').trim().toLowerCase();
    const range = Number(this.invoiceDateRange) || 0;

    this.filteredList = this.accountsList.filter((inv) => {
      if (term && !String(inv.invoiceNumber ?? '').toLowerCase().startsWith(term)) {
        return false;
      }
      if (!this.matchesCurrency(inv.currency)) {
        return false;
      }
      if (range > 0 && !this.dateCheck(inv.invoiceDate, range)) {
        return false;
      }
      return true;
    });

    const visibleAccounts = new Set(
      this.filteredList.map((inv) => inv.accounts?.accountNumber)
    );
    this.visibleAccountName = (this.accountName ?? []).filter((entry) =>
      visibleAccounts.has(entry[1])
    );

    this.p = 1;
    this.syncCheckAll();
  }

  /** Reset button — clears the search term and all filters. */
  clearSearch(): void {
    this.searchField = '';
    this.isCAD = true;
    this.isUSD = true;
    this.invoiceDateRange = 0;
    this.applyFilters();
  }

  onDateRangeChange(value: number | string): void {
    this.invoiceDateRange = Number(value) || 0;
    this.applyFilters();
  }

  private matchesCurrency(currency: string): boolean {
    if (this.isCAD && this.isUSD) {
      return currency === 'CAD' || currency === 'USD';
    }
    if (this.isCAD) { return currency === 'CAD'; }
    if (this.isUSD) { return currency === 'USD'; }
    return false;
  }

  /** True when the invoice date falls within the last `months` months. */
  private dateCheck(date: string, months: number): boolean {
    const from = new Date(date);
    if (isNaN(from.getTime())) { return false; }
    const today = new Date();
    const diff =
      today.getMonth() - from.getMonth() +
      12 * (today.getFullYear() - from.getFullYear());
    return diff <= months;
  }

  // ─── Currency / Pay panel ────────────────────────────────────────────────

  setPaymentCurrency(type: 'cad' | 'usd'): void {
    this.paymentCurrency = type === 'cad' ? 'CAD' : 'USD';
  }

  getSelectedCADTotal(): number {
    const total = this.accountsList
      .filter((inv) => inv.isChecked && inv.currency === 'CAD')
      .reduce((sum, inv) => sum + (parseFloat(inv.paymentAmount) || 0), 0);
    return parseFloat(total.toFixed(2));
  }

  getSelectedUSDTotal(): number {
    const total = this.accountsList
      .filter((inv) => inv.isChecked && inv.currency === 'USD')
      .reduce((sum, inv) => sum + (parseFloat(inv.paymentAmount) || 0), 0);
    return parseFloat(total.toFixed(2));
  }

  getSelectedInvoiceLength(): number {
    return this.accountsList.filter(
      (inv) => inv.isChecked && inv.currency === this.paymentCurrency
    ).length;
  }

  // ─── Invoice selection ───────────────────────────────────────────────────

  updateInvoiceSelection(invoice: InvoiceItem): void {
    if (invoice.isChecked) {
      invoice.paymentAmount = parseFloat(String(invoice.balanceDue)).toFixed(2);
      invoice.payAmount = invoice.paymentAmount;
      this.syncCheckAll();
    } else {
      invoice.paymentAmount = '0.00';
      invoice.payAmount = '0.00';
      this.checkAllInvoices = false;
    }
  }

  updatePaymentAmountInvoice(invoice: InvoiceItem): void {
    invoice.payAmount = invoice.paymentAmount;
  }

  /** Select / deselect all invoices currently visible after filtering. */
  selctAllInvoices(): void {
    this.filteredList.forEach((inv) => {
      inv.isChecked = this.checkAllInvoices;
      inv.paymentAmount = this.checkAllInvoices
        ? parseFloat(String(inv.balanceDue)).toFixed(2)
        : '0.00';
      inv.payAmount = inv.paymentAmount;
    });
  }

  /** Sets checkAllInvoices only when every visible row is checked. */
  private syncCheckAll(): void {
    this.checkAllInvoices =
      this.filteredList.length > 0 && this.filteredList.every((inv) => inv.isChecked);
  }

  // ─── Pay Now ─────────────────────────────────────────────────────────────

  viewCart(): void {
    if (!this.paymentCurrency) { return; }
    const total = this.paymentCurrency === 'CAD'
      ? this.getSelectedCADTotal()
      : this.getSelectedUSDTotal();
    if (total <= 0) {
      this.displayBalDueErrMsg = true;
      return;
    }
    this.displayBalDueErrMsg = false;
    const selectedInvoices = this.accountsList.filter(
      (inv) => inv.isChecked && inv.currency === this.paymentCurrency
    );
    this.payNowClicked.emit({ invoices: selectedInvoices, currency: this.paymentCurrency });
  }

  // ─── PDF ─────────────────────────────────────────────────────────────────

  getInvoicePDF(invoiceNumber: string): void {
    this.requestInvoicePDF.emit(invoiceNumber);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  getInvlocesCount(list: InvoiceItem[]): number {
    return list.filter((inv) => !!inv.invoiceNumber).length;
  }

  selectCheckBox(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLInputElement).click();
  }
}